import React, { useState, useEffect } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';

// Roles = identity + which portal experience a person gets. WHAT each role can
// access (module permissions) is managed on the separate Permissions page.

const BASE_TYPES = [
  { value:'admin',      label:'Master Admin (full access + manage users/roles/masters)' },
  { value:'it_head',    label:'IT Head (all campuses; create/assign tasks, approve delays)' },
  { value:'it_lead',    label:'IT Lead (campus IT lead; campus-scoped; uses the Permissions matrix)' },
  { value:'it_staff',   label:'Campus IT (campus-scoped; uses the Permissions matrix)' },
  { value:'requester',  label:'Teaching Staff portal (raise & track own tickets)' },
  { value:'principal',  label:'HOS dashboard (campus, read-only)' },
  { value:'management', label:'Management dashboard (all campuses, read-only)' },
  { value:'coordinator',label:'Coordinator dashboard (department tickets, read-only)' },
  { value:'finance',    label:'Finance (staff portal + Fee Dashboard and Fee Report Upload)' },
];
const BASE_LABEL = Object.fromEntries(BASE_TYPES.map(b => [b.value, b.label.split(' (')[0]]));
const usesPerms = bt => bt === 'it_staff' || bt === 'it_head' || bt === 'it_lead' || bt === 'finance';

export default function Roles() {
  const { isAdmin } = useAuth();
  const [roles,   setRoles]   = useState([]);
  const [modal,   setModal]   = useState(false);
  const [editing, setEditing] = useState(null);
  const [form,    setForm]    = useState({ name:'', description:'', base_type:'it_staff' });
  const [busy,    setBusy]    = useState(false);
  const [err,     setErr]     = useState('');

  const load = async () => { try { const r = await api.get('/master/roles'); setRoles(r.data); } catch(e) {} };
  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  if (!isAdmin) return <div style={{ padding:40, color:'var(--text2)', textAlign:'center' }}>Admin access required</div>;

  const openNew  = () => { setForm({ name:'', description:'', base_type:'it_staff' }); setEditing(null); setErr(''); setModal(true); };
  const openEdit = role => {
    setForm({ name: role.name, description: role.description || '', base_type: role.base_type || 'it_staff',
      active: role.active, is_builtin: role.is_builtin });
    setEditing(role); setErr(''); setModal(true);
  };

  const save = async () => {
    if (!form.name.trim()) { setErr('Role name is required'); return; }
    setBusy(true); setErr('');
    try {
      // NOTE: we never send `permissions` here — role identity only. Access is
      // edited on the Permissions page, so saving a role won't touch its access.
      if (editing) {
        await api.put(`/master/roles/${editing.id}`, {
          name: form.name.trim(), description: form.description.trim(),
          active: form.active, base_type: form.base_type });
      } else {
        await api.post('/master/roles', {
          name: form.name.trim(), description: form.description.trim(), base_type: form.base_type });
      }
      setModal(false); load();
    } catch (e) { setErr(e.response?.data?.error || 'Save failed'); }
    finally { setBusy(false); }
  };

  const toggleActive = async role => {
    if (role.is_builtin) return;
    try { await api.put(`/master/roles/${role.id}`, { active: !role.active }); load(); } catch(e) {}
  };

  return (
    <div>
      <div className="flex-gap" style={{ justifyContent:'space-between', marginBottom:20 }}>
        <div>
          <h2 style={{ fontSize:18, fontWeight:600, marginBottom:4 }}>Role Management</h2>
          <p style={{ fontSize:13, color:'var(--text2)' }}>Create roles here; set what each can access under <b>Permissions</b>.</p>
        </div>
        <button className="btn btn-primary" onClick={openNew}>+ New Role</button>
      </div>

      <div style={{ background:'rgba(79,142,247,0.08)', border:'1px solid rgba(79,142,247,0.2)', borderRadius:'var(--radius)', padding:'10px 14px', marginBottom:20, fontSize:13, color:'var(--text2)', lineHeight:1.5 }}>
        <strong style={{ color:'var(--accent)' }}>How it works:</strong> A role has a <strong>Base Experience</strong> (which screen the user gets). For Campus IT / IT Head roles, open the <strong>Permissions</strong> menu to choose which modules they can View, Create and Edit. Assign roles to people under <strong>Users</strong>.
      </div>

      {!roles.length ? (
        <div className="empty-state"><h3>No roles created yet</h3><p>Create your first role to start managing access.</p></div>
      ) : (
        <div className="card" style={{ padding:0, overflow:'hidden' }}>
          <div className="table-wrap"><table>
            <thead><tr><th>Role</th><th>Base Experience</th><th>Status</th><th>Action</th></tr></thead>
            <tbody>
              {roles.map(role => (
                <tr key={role.id} style={{ opacity: role.active ? 1 : 0.55 }}>
                  <td>
                    <div style={{ fontWeight:500 }}>{role.name}{role.is_builtin && <span style={{ fontSize:10, color:'var(--text3)', marginLeft:6 }}>(built-in)</span>}</div>
                    {role.description && <div style={{ fontSize:11, color:'var(--text2)' }}>{role.description}</div>}
                  </td>
                  <td style={{ fontSize:13 }}>{BASE_LABEL[role.base_type] || role.base_type}
                    {usesPerms(role.base_type) && <div style={{ fontSize:11, color:'var(--text2)' }}>uses module permissions</div>}</td>
                  <td><span className={`badge ${role.active ? 'badge-completed' : 'badge-faulty'}`}>{role.active ? 'Active' : 'Inactive'}</span></td>
                  <td>
                    <div className="flex-gap" style={{ gap:6 }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => openEdit(role)}>Edit</button>
                      {!role.is_builtin && (
                        <button className={`btn btn-sm ${role.active ? 'btn-danger' : 'btn-secondary'}`} onClick={() => toggleActive(role)}>
                          {role.active ? 'Deactivate' : 'Activate'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      )}

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && false && setModal(false)}>
          <div className="modal" style={{ maxWidth:560 }}>
            <div className="modal-header">
              <span className="modal-title">{editing ? `Edit Role — ${editing.name}` : 'Create New Role'}</span>
              <button className="modal-close" onClick={() => setModal(false)}>×</button>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Role Name *</label>
                <input value={form.name} disabled={form.is_builtin}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Campus IT — Backup Operator" />
                {form.is_builtin && <div style={{ fontSize:11, color:'var(--text2)', marginTop:4 }}>Built-in role — name & experience are fixed.</div>}
              </div>
              <div className="form-group">
                <label>Description <span style={{ fontSize:11, color:'var(--text2)' }}>(optional)</span></label>
                <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="e.g. Adds staff/students, runs backups" />
              </div>
            </div>

            <div className="form-group">
              <label>Base Experience *</label>
              <select value={form.base_type} disabled={form.is_builtin}
                onChange={e => setForm(f => ({ ...f, base_type: e.target.value }))}>
                {BASE_TYPES.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
              </select>
            </div>

            {editing && !form.is_builtin && (
              <div className="form-group">
                <label>Status</label>
                <select value={form.active ? 'active' : 'inactive'} onChange={e => setForm(f => ({ ...f, active: e.target.value === 'active' }))}>
                  <option value="active">Active</option><option value="inactive">Inactive</option>
                </select>
              </div>
            )}

            {usesPerms(form.base_type) && (
              <div style={{ fontSize:12, color:'var(--text2)', background:'var(--bg3)', borderRadius:'var(--radius)', padding:'10px 12px', marginBottom:14 }}>
                After saving, open <b>Permissions</b> to choose which modules this role can access.
              </div>
            )}

            {err && <div className="err-msg">{err}</div>}
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : editing ? 'Save Changes' : 'Create Role'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
